// Copyright (c) Docugami, Inc. All rights reserved.

import { useMemo } from 'react';
import { ICommandButton } from 'components/cards/CommandButton';
import { DgWordCanvas } from 'components/doc_hosting/doc_hosts/word/v3/DgWordCanvas';
import { useRemoveClauseCommands, getInsertClauseButton, getRemoveClauseFromLibraryButton, getRemoveClauseButton, useInsertClauseCommand, useAddVariantCommand, getAddVariantButton, emptyCommand } from 'hooks/ClauseCommands';
import { ICardletItem, Cardlet } from './ClauseCardletComponents';
import { createFolderId } from 'components/file_management/file_helpers';
import { useAppSelector } from 'hooks/redux';
import { IClauseItem } from './ClauseList';
import { clauseSelectedGreen } from 'components/shell/presentation/AppStyles';

// Helper to transform dgml string into plain inner text
export function getTextForDgml(dgmlString: string) {
    const dgml = new DgWordCanvas(dgmlString, []).dgml;
    return dgml?.value ?? "";
}

export interface ClauseCardletProps {
    documentId: string;
    clauseItem: IClauseItem;
    isSelected: boolean;
    onSelect: (variantId?: string) => void;
    selectedVariantId: string;
}

export const ClauseCardlet = (props: ClauseCardletProps): JSX.Element => {
    const documents = useAppSelector(state => state.documents.indexed);

    const { clauseItem, isSelected } = props;

    const selectedVariantOrDefault = useMemo(() => !!props.selectedVariantId && isSelected ? clauseItem.variants.find(x => x.id === props.selectedVariantId) : clauseItem.variants[0], [isSelected, props.selectedVariantId, clauseItem]);
    const insertClause = useInsertClauseCommand(props.documentId, selectedVariantOrDefault?.dgml);
    const addVariant = useAddVariantCommand(props.documentId, clauseItem.label, (id) => props.onSelect(id));
    const { removeClause, removeClauseLabel } = useRemoveClauseCommands(createFolderId(props.documentId).clusterId, clauseItem.id, props.selectedVariantId);
    const commands: ICommandButton[] = useMemo(() => (
        [
            !props.isSelected ? getInsertClauseButton(insertClause) : emptyCommand,
            getAddVariantButton(addVariant),
            getRemoveClauseFromLibraryButton(removeClauseLabel)
        ]
    ), [insertClause, addVariant, removeClauseLabel, props.isSelected]);

    const items: ICardletItem[] = useMemo(() =>
        clauseItem.variants.map((clause, idx) => {
            const filename = documents[clause.documentId]?.clusterDocument?.filename;
            const infoText = filename ? <>This clause was added from the <b>{filename}</b> document</> : undefined;
            const commands: ICommandButton[] = idx === 0 && clauseItem.variants.length === 1 ? [getInsertClauseButton(insertClause)] :
                [getInsertClauseButton(insertClause), getRemoveClauseButton(removeClause)];

            return ({
                text: getTextForDgml(clause.dgml),
                selected: clause.id === props.selectedVariantId && props.isSelected,
                select: () => props.onSelect(clause.id),
                commands: commands,
                infoText: infoText
            })
        }), [clauseItem, props, insertClause, removeClause, documents]);

    function onSelect() {
        if (!props.isSelected) {
            // Default select first variant
            props.onSelect(clauseItem.variants[0]?.id)
        }
    }

    return (
        <Cardlet
            label={clauseItem.label}
            items={items}
            isSelected={isSelected}
            onSelect={onSelect}
            commands={commands}
            selectedColor={clauseSelectedGreen} />
    )
}
